import * as React from 'react'
import { observer } from 'mobx-react'
import { SplitPayoutDialogViewModel } from './SplitPayoutDialogViewModel'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetFooterButtonGroup,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetContentDescription,
  ModalSheetFooterSection,
} from 'components/ModalSheet'
import Button from 'components/Button'
import ErrorBlock from 'components/ErrorBlock'
import PayoutSplitAllocator, {
  TotalAllocatedStatus,
  AddAllocationButton,
} from 'jobs/views/PayoutSplitAllocator'
import styles from './SplitPayoutDialog.sass'
import { formatMoney } from '../../../../../utils/money-util'
import ContentLoading from '../../../../../components/ContentLoading'
import ContentError from '../../../../../components/ContentError'

interface SplitPayoutDialogProps {
  vm: SplitPayoutDialogViewModel
}

/**
 * The SplitPayoutDialog
 */
export default observer(function SplitPayoutDialog({
  vm,
}: SplitPayoutDialogProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnEsc
    >
      <>
        <ModalSheetHeader withCloseButton />
        <ModalSheetContent leftAligned>
          <ModalSheetContentBlock>
            <DialogCopy vm={vm} />
          </ModalSheetContentBlock>
          <ModalSheetContentBlock>
            {vm.initialize.match({
              pending: () => <ContentLoading compact />,
              rejected: (err) => <ContentError error={err} />,
              resolved: () => <PayoutSplitAllocator vm={vm.allocatorVM} />,
            })}
          </ModalSheetContentBlock>
        </ModalSheetContent>
        {vm.splitPayout.error && (
          <ErrorBlock tackOn="bottom" error={vm.splitPayout.error} />
        )}
        <TotalAllocatedStatus vm={vm.allocatorVM} />
        <ModalSheetFooter>
          <ModalSheetFooterSection>
            <AddAllocationButton vm={vm.allocatorVM} />
          </ModalSheetFooterSection>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button onClick={vm.close} disabled={vm.splitPayout.pending}>
              Close
            </Button>
            <Button
              type="primary"
              disabled={vm.splitPayout.pending || !vm.allocatorVM.isSplitValid}
              loading={vm.splitPayout.pending}
              onClick={vm.splitPayout}
            >
              Split Payout
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </>
    </ModalSheet>
  )
})

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour12: true,
  hour: 'numeric',
  minute: 'numeric',
})

/**
 * Renders the dialog copy based on the situation.
 */
const DialogCopy = observer(function DialogCopy({
  vm,
}: {
  vm: SplitPayoutDialogViewModel
}) {
  const situation = vm.existingSplit.situation

  switch (situation.type) {
    case 'Manual': {
      return (
        <>
          <ModalSheetContentTitle>Edit split</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            A split payout of{' '}
            <Emphasize>{formatMoney(vm.allocatorVM.payoutTotal)}</Emphasize> as
            last edited by{' '}
            <Emphasize>{situation.allocatedBy.displayName}</Emphasize> on{' '}
            {renderTimestamp(situation.allocatedAt)}.
          </ModalSheetContentDescription>
        </>
      )
    }

    case 'Auto': {
      return (
        <>
          <ModalSheetContentTitle>Edit auto split</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            A split payout of{' '}
            <Emphasize>{formatMoney(vm.allocatorVM.payoutTotal)}</Emphasize> has
            been automatically adjusted by the system on{' '}
            {renderTimestamp(situation.adjustedAt)} and allocated to the
            selected Pros.
          </ModalSheetContentDescription>
        </>
      )
    }

    case 'Default': {
      return (
        <>
          <ModalSheetContentTitle>Split payout</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Split payout of{' '}
            <Emphasize>{formatMoney(vm.allocatorVM.payoutTotal)}</Emphasize>{' '}
            with selected Pros.
          </ModalSheetContentDescription>
        </>
      )
    }
  }
})

function renderTimestamp(adjustedAt: Date) {
  return (
    <>
      <Emphasize>{dateFormatter.format(adjustedAt)}</Emphasize> at{' '}
      <Emphasize>{timeFormatter.format(adjustedAt)}</Emphasize>
    </>
  )
}

/**
 * Emphasize
 */
function Emphasize({ children }: { children?: React.ReactNode }) {
  return <span className={styles.emphasize}>{children}</span>
}
